import type { ReactNode } from 'react';
import classNames from 'classnames';

import type { ButtonVariant } from '@/components/atoms/Buttons';
import Tooltip from '@/components/atoms/Tooltip';
import { Menu, MenuContent, MenuTrigger } from '@/components/molecules/Menu';

import styles from './DropDownPanel.module.scss';

export interface DropDownPanelProps {
    /** Names the icon-only trigger, and is its tooltip unless `tooltip` says more. */
    label: string;
    tooltip?: ReactNode;
    icon: ReactNode;
    children?: ReactNode;
    variant?: ButtonVariant;
    trackingId?: string;
    /** Analytics fallback when there is no `trackingId`. */
    name?: string;
    /** `end` lines the panel up with the trigger's far edge. */
    align?: 'start' | 'end';
    /** Controlled open state; leave it out and the panel keeps its own. */
    isOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    /** Class on the panel itself. */
    className?: string;
}

/**
 * An icon button that opens a panel of its own content, such as a form, below it. A Menu
 * panel: Menu positions it, closes it on Escape or a click outside, and keeps the
 * keyboard inside it the browser's.
 */
export function DropDownPanel(props: DropDownPanelProps) {
    const {
        label,
        tooltip = label,
        icon,
        children,
        variant = 'secondary',
        trackingId,
        name,
        align = 'end',
        isOpen,
        onOpenChange,
        className,
    } = props;

    return (
        <Menu kind="panel" isOpen={isOpen} onOpenChange={onOpenChange}>
            <Tooltip content={tooltip}>
                <MenuTrigger
                    variant={variant}
                    size="small"
                    iconOnly
                    aria-label={label}
                    aria-haspopup={false}
                    icon={icon}
                    trackingId={trackingId}
                    name={name}
                />
            </Tooltip>
            <MenuContent
                align={align}
                role="group"
                aria-label={label}
                className={classNames(styles.panel, className)}>
                <div className={styles.body}>{children}</div>
            </MenuContent>
        </Menu>
    );
}

export default DropDownPanel;
